import { Chip, Divider, Link, ListItemIcon, Menu, MenuItem, Modal, Stack, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import Badge from '@mui/material/Badge'
import AccountCircle from '@mui/icons-material/AccountCircle'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import ChatIcon from '@mui/icons-material/Chat'
import { useTranslation } from 'react-i18next'
import React, { useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IUser } from '../interface/IUser'
import DarkMode from '@mui/icons-material/DarkMode'
import Language from '@mui/icons-material/Language'
import LightMode from '@mui/icons-material/LightMode'
import SettingsBrightness from '@mui/icons-material/SettingsBrightness'
import Logout from '@mui/icons-material/Logout'
import { setTheme } from '../source/slice/ThemeSlice'
import { setWeekStart } from '../source/slice/WeekStartSlice'
import { useMsal } from '@azure/msal-react'
import logo from '../img/logo.svg'
import { WorkdayIcon } from '../img/workday'
import mbtiLogo from '../img/mbti-logo.svg'
import MailIcon from '@mui/icons-material/Mail'
import GitHubIcon from '@mui/icons-material/GitHub'
import LaunchIcon from '@mui/icons-material/Launch'
import InfoIcon from '@mui/icons-material/Info'
import DateRangeIcon from '@mui/icons-material/DateRange'
import Report from './Reports/Report'
import PMJournal from './PMJournal/PMJournal'
import ReactMarkdown from 'react-markdown'
import OperationPeriods from './MonthlyClosings'
import CloseIcon from '@mui/icons-material/Close'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import axios, { AxiosRequestConfig } from 'axios'
import { useRefreshUser } from '../utils/useRefreshUser'
import UserImpersonation from './UserImpersonation/UserImpersonation'

import legalDisclosureDe from '../legal/LegalDisclosureGerman.md?raw'
import legalDisclosureEn from '../legal/LegalDisclosureEnglish.md?raw'

import privacyPolicyDe from '../legal/PrivacyPolicyGerman.md?raw'
import privacyPolicyEn from '../legal/PrivacyPolicyEnglish.md?raw'

type NavigationProps = {
    onOpenQuickstart?: () => void
}

function Navigation({ onOpenQuickstart }: NavigationProps) {
    const { t, i18n } = useTranslation()
    const dispatch = useDispatch()
    const user: IUser = useSelector((state: any) => state.user?.value)
    const theme = useSelector((state: any) => state.theme?.value)
    const weekStart = useSelector((state: any) => state.weekStart?.value)
    const { refreshUser } = useRefreshUser()
    const { instance } = useMsal()

    const [supportModalOpen, setSupportModalOpen] = useState<boolean>(false)
    const handleModalClick = () => {
        setSupportModalOpen(true)
    }
    const handleModalClose = () => {
        setSupportModalOpen(false)
    }

    const [legalModalOpen, setLegalModalOpen] = useState<boolean>(false)
    const handleLegalModalOpen = () => {
        setLegalModalOpen(true)
    }
    const handleLegalModalClose = () => {
        setLegalModalOpen(false)
    }

    const [privacyModalOpen, setPrivacyModalOpen] = useState<boolean>(false)
    const handlePrivacyModalOpen = () => {
        setPrivacyModalOpen(true)
    }
    const handlePrivacyModalClose = () => {
        setPrivacyModalOpen(false)
    }

    const [pmJournalTooltipOpen, setPmJournalTooltipOpen] = useState(false)
    const pmJournalTooltipSuppressRef = useRef(false)
    const [operationalPeriodsModalOpen, setOperationalPeriodsModalOpen] = useState<boolean>(false)
    const handleOperationalPeriodsModalOpen = () => setOperationalPeriodsModalOpen(true)
    const handleOperationalPeriodsModalClose = () => setOperationalPeriodsModalOpen(false)

    const changeLanguage = (lng: string) => {
        i18n.changeLanguage(lng)
    }

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const navMenuOpen = Boolean(anchorEl)
    const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const handleMenuClose = () => {
        setAnchorEl(null)
    }

    const changeTheme = (color: string | null) => {
        if (!color) return
        dispatch(setTheme(color))
    }

    const changeWeekStart = (value: string | null) => {
        if (!value) return
        dispatch(setWeekStart(value))
    }

    const logout = async () => {
        await instance.logoutRedirect()
        await instance.logoutPopup()
    }

    const handleStopImpersonation = async () => {
        try {
            const config: AxiosRequestConfig = {
                headers: { Authorization: `Bearer ${user.token}` },
            }
            await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/Impersonation`, config)
            await refreshUser()
        } catch (error) {
            console.log(error)
        }
    }

    const legalDisclosureMarkdown = i18n.language === 'de' ? legalDisclosureDe : legalDisclosureEn
    const privacyPolicyMarkdown = i18n.language === 'de' ? privacyPolicyDe : privacyPolicyEn

    return (
        <Box sx={{ flexGrow: 1 }}>
            <AppBar
                position="static"
                sx={(theme) => ({
                    backgroundColor: theme.palette.common.black,
                    backgroundImage: 'none',
                    boxShadow: 'none',
                })}
            >
                <Toolbar style={{ gap: 10 }}>
                    <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                        <img src={mbtiLogo} alt="MBTI-Logo" style={{ height: 50 }} />
                    </Box>
                    <img src={logo} alt="Logo" style={{ width: 32, height: 32 }} />

                    <Typography variant="h5" component="div" sx={{ fontFamily: 'MB Corpo A Title', flexGrow: 1 }}>
                        Time Tracking
                    </Typography>
                    <Chip label="Confidential" color="error" variant="outlined" />
                    <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                        <Tooltip title={t('navigation.github')}>
                            <Link href="https://mercedes-benz.ghe.com/homeoftech/timetracking" underline="none" target="_blank" rel="noopener" color="inherit">
                                <IconButton size="large" color="inherit">
                                    <Badge>
                                        <GitHubIcon />
                                    </Badge>
                                </IconButton>
                            </Link>
                        </Tooltip>
                        <Tooltip title={t('navigation.reports')}>
                            <Badge>
                                <Report />
                            </Badge>
                        </Tooltip>
                        {user?.isPM && (
                            <Tooltip
                                title={t('navigation.pmJournal')}
                                open={pmJournalTooltipOpen}
                                onOpen={() => {
                                    if (!pmJournalTooltipSuppressRef.current) setPmJournalTooltipOpen(true)
                                }}
                                onClose={() => setPmJournalTooltipOpen(false)}
                                disableFocusListener
                            >
                                <span
                                    onClick={() => {
                                        pmJournalTooltipSuppressRef.current = true
                                        setPmJournalTooltipOpen(false)
                                        setTimeout(() => {
                                            pmJournalTooltipSuppressRef.current = false
                                        }, 600)
                                    }}
                                >
                                    <PMJournal />
                                </span>
                            </Tooltip>
                        )}
                        {user?.isAdmin && <UserImpersonation />}
                        <Tooltip title={t('navigation.workday')}>
                            <Link href="https://wd3.myworkday.com/mercedesbenztechinnovation/d/home.htmld" underline="none" target="_blank" rel="noopener" color="inherit">
                                <IconButton size="large" color="inherit">
                                    <Badge>
                                        <WorkdayIcon />
                                    </Badge>
                                </IconButton>
                            </Link>
                        </Tooltip>

                        <Tooltip title={t('navigation.helpcenter')}>
                            <Link href="https://corpdir.sharepoint.com/sites/mbti-apps-and-tools/SitePages/Navision.aspx#hilfeartikel" underline="none" target="_blank" rel="noopener" color="inherit">
                                <IconButton size="large" color="inherit">
                                    <Badge>
                                        <QuestionMarkIcon />
                                    </Badge>
                                </IconButton>
                            </Link>
                        </Tooltip>

                        <Tooltip title={t('navigation.support.title')}>
                            <IconButton size="large" color="inherit" onClick={handleModalClick}>
                                <Badge>
                                    <ChatIcon />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                        <Modal open={supportModalOpen} onClose={handleModalClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: 'calc(100% - 32px)',
                                    maxWidth: 800,
                                    height: 'calc(100% - 32px)',
                                    maxHeight: 'fit-content',
                                    overflow: 'auto',
                                    bgcolor: 'background.paper',
                                    border: 0,
                                    borderRadius: 1,
                                    boxShadow: 24,
                                    p: {
                                        xs: 2,
                                        sm: 4,
                                    },
                                }}
                            >
                                <Typography id="modal-modal-title" variant="h6" component="h2">
                                    {t('navigation.support.title')}
                                </Typography>
                                <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                                    {t('navigation.support.description')}
                                </Typography>
                                <Typography sx={{ mt: 4 }}>{t('navigation.support.hrHeader')}</Typography>
                                <Typography sx={{ mt: 1 }}>
                                    <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
                                        <MailIcon color="inherit" fontSize="small" />
                                        <Link href="mailto:mbti-hr-services@mercedes-benz.com" target="_blank" rel="noopener" underline="hover">
                                            {t('navigation.support.hrLink')}
                                        </Link>
                                    </Stack>
                                </Typography>
                                <Typography sx={{ mt: 4 }}>{t('navigation.support.pfcHeader')}</Typography>
                                <Typography sx={{ mt: 1 }}>
                                    <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
                                        <MailIcon color="inherit" fontSize="small" />
                                        <Link href="mailto:mbti-pfc@mercedes-benz.com" target="_blank" rel="noopener" underline="hover">
                                            {t('navigation.support.pfcLink')}
                                        </Link>
                                    </Stack>
                                </Typography>
                                <Typography sx={{ mt: 4 }}>{t('navigation.support.controllingHeader')}</Typography>
                                <Typography sx={{ mt: 1 }}>
                                    <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
                                        <MailIcon color="inherit" fontSize="small" />
                                        <Link href="mailto:mbti-controlling@mercedes-benz.com" target="_blank" rel="noopener" underline="hover">
                                            {t('navigation.support.controllingLink')}
                                        </Link>
                                    </Stack>
                                </Typography>
                                <Typography sx={{ mt: 4 }}>{t('navigation.support.serviceportalHeader')}</Typography>
                                <Typography sx={{ mt: 1 }}>
                                    <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
                                        <LaunchIcon color="inherit" fontSize="small" />
                                        <Link
                                            href="https://mbti-serviceportal.app.corpintra.net/wm/app-SelfServicePortalTSS/landing-page/0da8c041-fbc7-ea11-4e91-005056b1ba68?view-options=%7B%22dialogId%22:%222db6f3ef-9b9b-eb11-9181-005056b1b743%22,%22type%22:%22SVMChangeRequestType%22,%22viewType%22:%22new%22%7D&presetParams=%7B%22SPSActivityClassBase%22:%7B%22CUSTSSPType%22:%22110%22,%20%22CUST_FOTicketType%22:%20%2210%22%7D%7D"
                                            target="_blank"
                                            rel="noopener"
                                            underline="hover"
                                        >
                                            {t('navigation.support.serviceportalLink')}
                                        </Link>
                                    </Stack>
                                    <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
                                        <Typography>{t('navigation.support.serviceportalInfo')}</Typography>
                                        <MailIcon color="inherit" fontSize="small" />
                                        <Link href="mailto:mbti-tickets.pool-id@mercedes-benz.com?subject=Request FO Timetracking - <describe your problem>&cc=mbti-navteam@mercedes-benz.com" target="_blank" rel="noopener" underline="hover">
                                            Support Mail
                                        </Link>
                                    </Stack>
                                </Typography>
                                <Typography sx={{ mt: 4 }}>{t('navigation.support.githubHeader')}</Typography>
                                <Typography sx={{ mt: 1 }}>
                                    <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
                                        <GitHubIcon color="inherit" fontSize="small" />
                                        <Link href="https://mercedes-benz.ghe.com/homeoftech/timetracking" target="_blank" rel="noopener" underline="hover">
                                            {t('navigation.github')}
                                        </Link>
                                    </Stack>
                                </Typography>
                            </Box>
                        </Modal>
                        <IconButton size="large" edge="end" aria-haspopup="true" color="inherit" aria-controls={navMenuOpen ? 'basic-menu' : undefined} aria-expanded={navMenuOpen ? 'true' : undefined} onClick={handleMenuClick}>
                            <AccountCircle />
                        </IconButton>
                        <Menu id="basic-menu" anchorEl={anchorEl} open={navMenuOpen} onClose={handleMenuClose} slotProps={{ list: { 'aria-labelledby': 'basic-button' } }}>
                            <Stack direction="row" sx={{ alignItems: 'center', gap: 1, px: 2, pt: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    {user?.worker?.mserp_name}
                                </Typography>
                            </Stack>
                            <Stack direction="row" sx={{ alignItems: 'center', gap: 1, px: 2 }}>
                                <Typography variant="body2" sx={{ color: 'grey' }}>
                                    {user?.resource?.mserp_resourceid}
                                </Typography>
                            </Stack>
                            <Stack direction="row" sx={{ alignItems: 'center', gap: 1, px: 2, pb: 1 }}>
                                <Typography variant="body2" sx={{ color: 'grey' }}>
                                    {user?.userid}
                                </Typography>
                            </Stack>
                            <Divider style={{ marginTop: '8px', marginBottom: '4px' }} />
                            <Typography variant="body2" sx={{ px: 2, pt: 0.5, fontWeight: 'bold' }}>
                                {t('navigation.settings')}
                            </Typography>
                            <Typography variant="caption" sx={{ px: 2, pt: 0.5, color: 'text.secondary' }}>
                                {t('navigation.settingsLanguage')}
                            </Typography>
                            <Stack sx={{ px: 2, py: 0.5 }}>
                                <ToggleButtonGroup size="small" exclusive onChange={(e, val: string) => changeLanguage(val)} aria-label="text alignment" sx={{ width: '100%' }}>
                                    <ToggleButton value="de" aria-label="left aligned" selected={i18n.language === 'de'} sx={{ flex: 1, gap: '6px' }}>
                                        <Language fontSize="small" /> Deutsch
                                    </ToggleButton>
                                    <ToggleButton value="en" aria-label="right aligned" selected={i18n.language === 'en'} sx={{ flex: 1, gap: '6px' }}>
                                        <Language fontSize="small" /> English
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Stack>
                            <Typography variant="caption" sx={{ px: 2, pt: 0.5, color: 'text.secondary' }}>
                                {t('navigation.settingsWeekStart')}
                            </Typography>
                            <Stack sx={{ px: 2, py: 0.5 }}>
                                <ToggleButtonGroup size="small" exclusive onChange={(e, val: string | null) => changeWeekStart(val)} aria-label="week start selection" sx={{ width: '100%' }}>
                                    <ToggleButton value="monday" aria-label="monday" selected={weekStart === 'monday'} sx={{ flex: 1, gap: '6px' }}>
                                        <DateRangeIcon fontSize="small" /> {t('navigation.weekStartMonday')}
                                    </ToggleButton>
                                    <ToggleButton value="sunday" aria-label="sunday" selected={weekStart === 'sunday'} sx={{ flex: 1, gap: '6px' }}>
                                        <DateRangeIcon fontSize="small" /> {t('navigation.weekStartSunday')}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Stack>
                            <Typography variant="caption" sx={{ px: 2, pt: 0.5, color: 'text.secondary' }}>
                                {t('navigation.settingsTheme')}
                            </Typography>
                            <Stack sx={{ px: 2, py: 0.5, pb: 1 }}>
                                <ToggleButtonGroup size="small" exclusive onChange={(e, val: string | null) => changeTheme(val)} aria-label="theme selection" sx={{ width: '100%' }}>
                                    <ToggleButton value="system" aria-label="system" selected={theme === 'system'} sx={{ flex: 1, gap: '6px', px: 2 }}>
                                        <SettingsBrightness fontSize="small" /> {t('navigation.systemMode')}
                                    </ToggleButton>
                                    <ToggleButton value="light" aria-label="light" selected={theme === 'light'} sx={{ flex: 1, gap: '6px', px: 2 }}>
                                        <LightMode fontSize="small" /> {t('navigation.lightMode')}
                                    </ToggleButton>
                                    <ToggleButton value="dark" aria-label="dark" selected={theme === 'dark'} sx={{ flex: 1, gap: '6px', px: 2 }}>
                                        <DarkMode fontSize="small" /> {t('navigation.darkMode')}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Stack>
                            <Divider style={{ marginTop: '4px', marginBottom: '8px' }} />
                            <MenuItem
                                onClick={() => {
                                    handleMenuClose()
                                    onOpenQuickstart?.()
                                }}
                            >
                                <ListItemIcon>
                                    <QuestionMarkIcon fontSize="small" />
                                </ListItemIcon>
                                {t('navigation.quickstart')}
                            </MenuItem>

                            <MenuItem
                                onClick={() => {
                                    handleMenuClose()
                                    handleOperationalPeriodsModalOpen()
                                }}
                            >
                                <ListItemIcon>
                                    <DateRangeIcon fontSize="small" />
                                </ListItemIcon>
                                {t('navigation.monthlyClosings')}
                            </MenuItem>

                            <MenuItem component="a" href="https://mercedes-benz.ghe.com/homeoftech/timetracking/releases" target="_blank">
                                <ListItemIcon>
                                    <GitHubIcon fontSize="small" />
                                </ListItemIcon>
                                {t('navigation.releaseversion')}
                                {process.env.REACT_APP_VERSION}
                            </MenuItem>

                            <MenuItem
                                onClick={() => {
                                    handleMenuClose()
                                    handleLegalModalOpen()
                                }}
                            >
                                <ListItemIcon>
                                    <InfoIcon fontSize="small" />
                                </ListItemIcon>
                                {t('navigation.legalNotice')}
                            </MenuItem>

                            <MenuItem
                                onClick={() => {
                                    handleMenuClose()
                                    handlePrivacyModalOpen()
                                }}
                            >
                                <ListItemIcon>
                                    <InfoIcon fontSize="small" />
                                </ListItemIcon>
                                {t('navigation.privacyPolicy')}
                            </MenuItem>

                            <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
                            <MenuItem onClick={logout}>
                                <ListItemIcon>
                                    <Logout fontSize="small" />
                                </ListItemIcon>
                                {t('navigation.logout')}
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>

            {user?.worker && user?.userid !== user?.worker?.mserp_identityemail && (
                <Box
                    sx={{
                        bgcolor: 'warning.main',
                        color: 'warning.contrastText',
                        px: 2,
                        py: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        justifyContent: 'center',
                    }}
                >
                    <WarningAmberIcon fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {t('impersonation.banner', { name: user?.worker?.mserp_name, email: user?.worker?.mserp_identityemail })}
                    </Typography>
                    <IconButton size="small" onClick={handleStopImpersonation} sx={{ color: 'warning.contrastText', ml: 1 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}

            <Modal open={legalModalOpen} onClose={handleLegalModalClose} aria-labelledby="legal-modal-title" aria-describedby="legal-modal-description">
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 'calc(100% - 32px)',
                        maxWidth: 800,
                        height: 'calc(100% - 32px)',
                        maxHeight: 'fit-content',
                        overflow: 'auto',
                        bgcolor: 'background.paper',
                        border: 0,
                        borderRadius: 1,
                        boxShadow: 24,
                        p: {
                            xs: 2,
                            sm: 4,
                        },
                    }}
                >
                    <Box id="legal-modal-description" sx={{ mt: 2, '& h2': { mt: 2 }, '& h3': { mt: 2 }, '& p': { mt: 1 } }}>
                        <ReactMarkdown
                            components={{
                                h2: (props) => <Typography variant="h5" component="h3" gutterBottom {...props} />,
                                h3: (props) => <Typography variant="h6" component="h4" gutterBottom {...props} />,
                                p: (props) => <Typography variant="body2" sx={{ mb: 2 }} {...props} />,
                            }}
                        >
                            {legalDisclosureMarkdown}
                        </ReactMarkdown>
                    </Box>
                </Box>
            </Modal>

            <Modal open={privacyModalOpen} onClose={handlePrivacyModalClose} aria-labelledby="privacy-modal-title" aria-describedby="privacy-modal-description">
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 'calc(100% - 32px)',
                        maxWidth: 800,
                        height: 'calc(100% - 32px)',
                        maxHeight: 'fit-content',
                        overflow: 'auto',
                        bgcolor: 'background.paper',
                        border: 0,
                        borderRadius: 1,
                        boxShadow: 24,
                        p: {
                            xs: 2,
                            sm: 4,
                        },
                    }}
                >
                    <Box id="privacy-modal-description" sx={{ mt: 2, '& h2': { mt: 2 }, '& h3': { mt: 2 }, '& p': { mt: 1 } }}>
                        <ReactMarkdown
                            components={{
                                h2: (props) => <Typography variant="h5" component="h3" gutterBottom {...props} />,
                                h3: (props) => <Typography variant="h6" component="h4" gutterBottom {...props} />,
                                p: (props) => <Typography variant="body2" sx={{ mb: 2 }} {...props} />,
                                li: (props) => (
                                    <Typography variant="body2" component="li" sx={{ mb: 0.5 }}>
                                        {props.children}
                                    </Typography>
                                ),
                                ul: (props) => <Box component="ul" sx={{ pl: 3, mt: 1, mb: 2 }} {...props} />,
                            }}
                        >
                            {privacyPolicyMarkdown}
                        </ReactMarkdown>
                    </Box>
                </Box>
            </Modal>

            <OperationPeriods open={operationalPeriodsModalOpen} onClose={handleOperationalPeriodsModalClose} />
        </Box>
    )
}

export default Navigation
